// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useCallback, useMemo } from 'react';
import {
  BuildingOffice2Icon,
  TruckIcon,
  ClockIcon,
  ChartBarIcon,
  StarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  DocumentArrowDownIcon,
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TableCellsIcon,
  Squares2X2Icon,
  DocumentTextIcon,
  XMarkIcon,
  MapPinIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';

type TrendPeriod = 'week' | 'month' | 'year' | '2years';
type CardFilter = 'all' | 'total' | 'preferred' | 'atRisk';
type SortDirection = 'asc' | 'desc';
type ViewMode = 'cards' | 'list';

interface Supplier {
  id: string;
  name: string;
  code: string;
  category: string;
  region: string;
  totalShipments: number;
  onTimeDelivery: number; // DOT - Delivery On Time percentage
  completeOrders: number; // COT - Complete On Time percentage
  fillRate: number;
  qualityScore: number;
  responsiveness: number;
  overallScore: number;
  trend: 'up' | 'down' | 'stable';
  trendValue: number;
  status: 'preferred' | 'approved' | 'probation' | 'suspended';
  contractExpiry: string;
  lastDelivery: string;
  avgLeadTime: number;
  defectRate: number;
  monthlyVolume: number;
  yearlySpend: number;
}

interface PerformanceMetric {
  month: string;
  dot: number;
  cot: number;
  fillRate: number;
}

interface SupplierShipment {
  id: string;
  date: string;
  origin: string;
  destination: string;
  status: 'delivered' | 'in_transit' | 'delayed';
  pallets: number;
  onTime: boolean;
}

const mockSuppliers: Supplier[] = [
  {
    id: 'SUP-001',
    name: 'Midwest Manufacturing Co.',
    code: 'MMC-2019',
    category: 'Maintenance Supplies',
    region: 'Midwest',
    totalShipments: 1248,
    onTimeDelivery: 96.8,
    completeOrders: 98.2,
    fillRate: 97.5,
    qualityScore: 94,
    responsiveness: 92,
    overallScore: 95.7,
    trend: 'up',
    trendValue: 2.3,
    status: 'preferred',
    contractExpiry: '2025-12-31',
    lastDelivery: '2024-01-09',
    avgLeadTime: 5,
    defectRate: 0.8,
    monthlyVolume: 145,
    yearlySpend: 2450000,
  },
  {
    id: 'SUP-002',
    name: 'Texas Parts Supply',
    code: 'TPS-2018',
    category: 'Hardware',
    region: 'Southwest',
    totalShipments: 892,
    onTimeDelivery: 94.2,
    completeOrders: 95.8,
    fillRate: 96.1,
    qualityScore: 91,
    responsiveness: 88,
    overallScore: 93.0,
    trend: 'stable',
    trendValue: 0.5,
    status: 'approved',
    contractExpiry: '2024-06-30',
    lastDelivery: '2024-01-08',
    avgLeadTime: 7,
    defectRate: 1.2,
    monthlyVolume: 98,
    yearlySpend: 1820000,
  },
  {
    id: 'SUP-003',
    name: 'Pacific Imports Ltd.',
    code: 'PIL-2020',
    category: 'Heavy Equipment',
    region: 'West Coast',
    totalShipments: 456,
    onTimeDelivery: 88.5,
    completeOrders: 91.2,
    fillRate: 89.8,
    qualityScore: 86,
    responsiveness: 82,
    overallScore: 87.5,
    trend: 'down',
    trendValue: -3.2,
    status: 'probation',
    contractExpiry: '2024-03-31',
    lastDelivery: '2024-01-07',
    avgLeadTime: 21,
    defectRate: 2.8,
    monthlyVolume: 42,
    yearlySpend: 3200000,
  },
  {
    id: 'SUP-004',
    name: 'Northwest Electronics',
    code: 'NWE-2021',
    category: 'Electronics',
    region: 'Pacific Northwest',
    totalShipments: 678,
    onTimeDelivery: 92.1,
    completeOrders: 94.5,
    fillRate: 93.2,
    qualityScore: 96,
    responsiveness: 90,
    overallScore: 93.2,
    trend: 'up',
    trendValue: 1.8,
    status: 'approved',
    contractExpiry: '2025-09-30',
    lastDelivery: '2024-01-10',
    avgLeadTime: 12,
    defectRate: 0.5,
    monthlyVolume: 62,
    yearlySpend: 1560000,
  },
  {
    id: 'SUP-005',
    name: 'Southern Foods Inc.',
    code: 'SFI-2017',
    category: 'Safety Equipment',
    region: 'Southeast',
    totalShipments: 1567,
    onTimeDelivery: 97.5,
    completeOrders: 98.8,
    fillRate: 98.2,
    qualityScore: 95,
    responsiveness: 96,
    overallScore: 97.1,
    trend: 'up',
    trendValue: 0.8,
    status: 'preferred',
    contractExpiry: '2026-03-31',
    lastDelivery: '2024-01-10',
    avgLeadTime: 3,
    defectRate: 0.3,
    monthlyVolume: 178,
    yearlySpend: 980000,
  },
  {
    id: 'SUP-006',
    name: 'East Coast Distributors',
    code: 'ECD-2019',
    category: 'Packaging',
    region: 'Northeast',
    totalShipments: 2134,
    onTimeDelivery: 95.4,
    completeOrders: 96.9,
    fillRate: 97.8,
    qualityScore: 92,
    responsiveness: 94,
    overallScore: 95.4,
    trend: 'stable',
    trendValue: -0.2,
    status: 'approved',
    contractExpiry: '2025-06-30',
    lastDelivery: '2024-01-09',
    avgLeadTime: 4,
    defectRate: 0.9,
    monthlyVolume: 245,
    yearlySpend: 720000,
  },
  {
    id: 'SUP-007',
    name: 'Central Midwest Supply',
    code: 'CMS-2020',
    category: 'Fleet Supplies',
    region: 'Midwest',
    totalShipments: 987,
    onTimeDelivery: 91.8,
    completeOrders: 93.5,
    fillRate: 94.1,
    qualityScore: 88,
    responsiveness: 85,
    overallScore: 90.5,
    trend: 'down',
    trendValue: -1.5,
    status: 'approved',
    contractExpiry: '2024-09-30',
    lastDelivery: '2024-01-08',
    avgLeadTime: 6,
    defectRate: 1.8,
    monthlyVolume: 89,
    yearlySpend: 1340000,
  },
  {
    id: 'SUP-008',
    name: 'Atlantic Cargo Services',
    code: 'ACS-2016',
    category: 'Storage Equipment',
    region: 'Southeast',
    totalShipments: 234,
    onTimeDelivery: 85.2,
    completeOrders: 87.8,
    fillRate: 86.5,
    qualityScore: 82,
    responsiveness: 78,
    overallScore: 83.9,
    trend: 'down',
    trendValue: -4.1,
    status: 'suspended',
    contractExpiry: '2024-01-31',
    lastDelivery: '2024-01-05',
    avgLeadTime: 28,
    defectRate: 4.2,
    monthlyVolume: 18,
    yearlySpend: 890000,
  },
];

// Weekly performance data
const weeklyPerformanceData: PerformanceMetric[] = [
  { month: 'Week 1', dot: 93.2, cot: 95.1, fillRate: 94.5 },
  { month: 'Week 2', dot: 94.1, cot: 95.8, fillRate: 95.2 },
  { month: 'Week 3', dot: 93.8, cot: 95.5, fillRate: 94.8 },
  { month: 'Week 4', dot: 94.5, cot: 96.0, fillRate: 95.3 },
  { month: 'Week 5', dot: 93.5, cot: 95.2, fillRate: 94.6 },
  { month: 'Week 6', dot: 94.2, cot: 95.9, fillRate: 95.1 },
  { month: 'Week 7', dot: 94.8, cot: 96.3, fillRate: 95.6 },
  { month: 'Week 8', dot: 94.5, cot: 96.1, fillRate: 95.5 },
];

// Monthly performance data
const monthlyPerformanceData: PerformanceMetric[] = [
  { month: 'Aug', dot: 92.5, cot: 94.2, fillRate: 93.8 },
  { month: 'Sep', dot: 93.1, cot: 94.8, fillRate: 94.2 },
  { month: 'Oct', dot: 94.2, cot: 95.5, fillRate: 95.0 },
  { month: 'Nov', dot: 93.8, cot: 95.2, fillRate: 94.5 },
  { month: 'Dec', dot: 91.5, cot: 93.8, fillRate: 92.8 },
  { month: 'Jan', dot: 94.5, cot: 96.1, fillRate: 95.5 },
];

// Yearly performance data (by quarter)
const yearlyPerformanceData: PerformanceMetric[] = [
  { month: 'Q1 2023', dot: 91.8, cot: 93.5, fillRate: 92.8 },
  { month: 'Q2 2023', dot: 92.5, cot: 94.2, fillRate: 93.5 },
  { month: 'Q3 2023', dot: 93.2, cot: 95.0, fillRate: 94.2 },
  { month: 'Q4 2023', dot: 93.8, cot: 95.5, fillRate: 94.8 },
  { month: 'Q1 2024', dot: 94.5, cot: 96.1, fillRate: 95.5 },
];

// 2 Year performance data (by half-year)
const twoYearPerformanceData: PerformanceMetric[] = [
  { month: 'H1 2022', dot: 89.5, cot: 91.2, fillRate: 90.5 },
  { month: 'H2 2022', dot: 90.8, cot: 92.5, fillRate: 91.8 },
  { month: 'H1 2023', dot: 92.2, cot: 93.8, fillRate: 93.0 },
  { month: 'H2 2023', dot: 93.5, cot: 95.2, fillRate: 94.5 },
  { month: 'H1 2024', dot: 94.5, cot: 96.1, fillRate: 95.5 },
];

// Get performance data based on selected period
const getPerformanceDataByPeriod = (period: TrendPeriod): PerformanceMetric[] => {
  switch (period) {
    case 'week':
      return weeklyPerformanceData;
    case 'month':
      return monthlyPerformanceData;
    case 'year':
      return yearlyPerformanceData;
    case '2years':
      return twoYearPerformanceData;
    default:
      return monthlyPerformanceData;
  }
};

// Generate mock shipments for a supplier
const generateSupplierShipments = (supplier: Supplier): SupplierShipment[] => {
  const shipments: SupplierShipment[] = [];
  const destinations = ['Chicago, IL', 'Dallas, TX', 'Atlanta, GA', 'Seattle, WA', 'Miami, FL', 'Denver, CO'];
  const statuses: SupplierShipment['status'][] = ['delivered', 'delivered', 'delivered', 'in_transit', 'delayed'];

  for (let i = 0; i < 15; i++) {
    const date = new Date();
    date.setDate(date.getDate() - i * 2);
    const status = statuses[Math.floor(Math.random() * statuses.length)];

    shipments.push({
      id: `SHP-${supplier.code}-${1000 + i}`,
      date: date.toISOString().split('T')[0],
      origin: `${supplier.name.split(' ')[0]} Warehouse`,
      destination: destinations[Math.floor(Math.random() * destinations.length)],
      status,
      pallets: Math.floor(Math.random() * 20) + 5,
      onTime: status !== 'delayed',
    });
  }

  return shipments;
};

const SupplierScorecard: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [sortBy, setSortBy] = useState<'overallScore' | 'onTimeDelivery' | 'completeOrders'>('overallScore');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Quarter selector state
  const [showQuarterMenu, setShowQuarterMenu] = useState(false);
  const [selectedQuarter, setSelectedQuarter] = useState('Q4 2023');

  // Export dropdown state
  const [showExportMenu, setShowExportMenu] = useState(false);

  // Card filter state
  const [cardFilter, setCardFilter] = useState<CardFilter>('all');

  // Trend period state
  const [trendPeriod, setTrendPeriod] = useState<TrendPeriod>('month');

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('cards');

  // Shipments modal state
  const [showShipmentsModal, setShowShipmentsModal] = useState(false);
  const [shipmentsSupplier, setShipmentsSupplier] = useState<Supplier | null>(null);

  // Supplier export dropdown state
  const [showSupplierExportMenu, setShowSupplierExportMenu] = useState(false);

  // Apply card filter
  const applyCardFilter = (supplier: Supplier): boolean => {
    switch (cardFilter) {
      case 'total':
        return true;
      case 'preferred':
        return supplier.status === 'preferred';
      case 'atRisk':
        return supplier.status === 'probation' || supplier.status === 'suspended';
      case 'all':
      default:
        return true;
    }
  };

  const filteredSuppliers = mockSuppliers
    .filter((supplier) => {
      const matchesSearch =
        supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        supplier.code.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !statusFilter || supplier.status === statusFilter;
      const matchesRegion = !regionFilter || supplier.region === regionFilter;
      const matchesCardFilter = applyCardFilter(supplier);
      return matchesSearch && matchesStatus && matchesRegion && matchesCardFilter;
    })
    .sort((a, b) => {
      const comparison = a[sortBy] - b[sortBy];
      return sortDirection === 'desc' ? -comparison : comparison;
    });

  const regions = [...new Set(mockSuppliers.map((s) => s.region))];

  // Get performance data based on selected trend period
  const currentPerformanceData = useMemo(() => getPerformanceDataByPeriod(trendPeriod), [trendPeriod]);

  const getScoreColor = (score: number) => {
    if (score >= 95) return 'text-green-600 dark:text-green-400';
    if (score >= 90) return 'text-blue-600 dark:text-blue-400';
    if (score >= 85) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 95) return 'bg-green-500';
    if (score >= 90) return 'bg-blue-500';
    if (score >= 85) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusBadge = (status: Supplier['status']) => {
    const styles = {
      preferred: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      approved: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      probation: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      suspended: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const getTrendIcon = (trend: Supplier['trend'], value: number) => {
    if (trend === 'up') {
      return (
        <span className="flex items-center text-green-600 dark:text-green-400">
          <ArrowTrendingUpIcon className="w-4 h-4 mr-1" />
          +{value}%
        </span>
      );
    } else if (trend === 'down') {
      return (
        <span className="flex items-center text-red-600 dark:text-red-400">
          <ArrowTrendingDownIcon className="w-4 h-4 mr-1" />
          {value}%
        </span>
      );
    }
    return <span className="text-gray-500 dark:text-gray-400">{value > 0 ? '+' : ''}{value}%</span>;
  };

  const renderStars = (score: number) => {
    const fullStars = Math.floor(score / 20);
    const hasHalf = (score % 20) >= 10;
    const stars = [];

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<StarSolidIcon key={i} className="w-4 h-4 text-yellow-400" />);
      } else if (i === fullStars && hasHalf) {
        stars.push(<StarSolidIcon key={i} className="w-4 h-4 text-yellow-400 opacity-50" />);
      } else {
        stars.push(<StarIcon key={i} className="w-4 h-4 text-gray-300 dark:text-gray-600" />);
      }
    }
    return <div className="flex">{stars}</div>;
  };

  const stats = {
    totalSuppliers: mockSuppliers.length,
    preferred: mockSuppliers.filter((s) => s.status === 'preferred').length,
    avgDOT: Math.round((mockSuppliers.reduce((acc, s) => acc + s.onTimeDelivery, 0) / mockSuppliers.length) * 10) / 10,
    avgCOT: Math.round((mockSuppliers.reduce((acc, s) => acc + s.completeOrders, 0) / mockSuppliers.length) * 10) / 10,
    atRisk: mockSuppliers.filter((s) => s.status === 'probation' || s.status === 'suspended').length,
    avgScore: Math.round((mockSuppliers.reduce((acc, s) => acc + s.overallScore, 0) / mockSuppliers.length) * 10) / 10,
  };

  // Handle sort change with direction toggle
  const handleSortChange = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  // Export functions for main page
  const handleExportCSV = useCallback(() => {
    const headers = ['Supplier', 'Code', 'Category', 'Region', 'Status', 'DOT %', 'COT %', 'Fill Rate %', 'Quality Score', 'Overall Score', 'Total Shipments', 'Avg Lead Time', 'Defect Rate', 'Yearly Spend'];
    const rows = filteredSuppliers.map(s => [
      s.name, s.code, s.category, s.region, s.status, s.onTimeDelivery, s.completeOrders, s.fillRate, s.qualityScore, s.overallScore, s.totalShipments, s.avgLeadTime, s.defectRate, s.yearlySpend
    ]);
    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `supplier_scorecard_${selectedQuarter.replace(' ', '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportMenu(false);
  }, [filteredSuppliers, selectedQuarter]);

  const handleExportWord = useCallback(() => {
    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8"><title>Supplier Scorecard Report</title>
        <style>
          body { font-family: Calibri, Arial, sans-serif; padding: 20px; }
          h1 { color: #0d9488; border-bottom: 2px solid #0d9488; padding-bottom: 10px; }
          h2 { color: #374151; margin-top: 30px; }
          table { border-collapse: collapse; width: 100%; margin-top: 20px; }
          th { background-color: #0d9488; color: white; padding: 12px 8px; text-align: left; }
          td { border: 1px solid #d1d5db; padding: 10px 8px; }
          tr:nth-child(even) { background-color: #f3f4f6; }
          .summary { background-color: #f0fdfa; border: 1px solid #14b8a6; padding: 15px; margin: 20px 0; }
        </style>
      </head>
      <body>
        <h1>Supplier Scorecard Report - ${selectedQuarter}</h1>
        <p><strong>Generated:</strong> ${dateStr}</p>
        <div class="summary">
          <h3>Summary</h3>
          <p><strong>Total Suppliers:</strong> ${stats.totalSuppliers} | <strong>Preferred:</strong> ${stats.preferred} | <strong>At Risk:</strong> ${stats.atRisk}</p>
          <p><strong>Avg DOT:</strong> ${stats.avgDOT}% | <strong>Avg COT:</strong> ${stats.avgCOT}% | <strong>Avg Score:</strong> ${stats.avgScore}</p>
        </div>
        <h2>Supplier Details</h2>
        <table>
          <tr><th>Supplier</th><th>Status</th><th>DOT %</th><th>COT %</th><th>Fill Rate</th><th>Overall</th><th>Shipments</th></tr>
          ${filteredSuppliers.map(s => `<tr><td>${s.name}</td><td>${s.status}</td><td>${s.onTimeDelivery}%</td><td>${s.completeOrders}%</td><td>${s.fillRate}%</td><td>${s.overallScore}</td><td>${s.totalShipments}</td></tr>`).join('')}
        </table>
      </body></html>`;
    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `supplier_scorecard_${selectedQuarter.replace(' ', '_')}.doc`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportMenu(false);
  }, [filteredSuppliers, selectedQuarter, stats]);

  const handleExportPDF = useCallback(() => {
    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const printContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Supplier Scorecard</title>
      <style>
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 1100px; margin: 0 auto; }
        h1 { color: #0d9488; border-bottom: 3px solid #0d9488; padding-bottom: 15px; }
        table { border-collapse: collapse; width: 100%; margin-top: 15px; font-size: 11px; }
        th { background-color: #0d9488; color: white; padding: 10px 6px; text-align: left; }
        td { border: 1px solid #d1d5db; padding: 8px 6px; }
        tr:nth-child(even) { background-color: #f9fafb; }
        .summary-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 20px 0; }
        .summary-card { background: linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%); border: 1px solid #14b8a6; padding: 15px; border-radius: 8px; text-align: center; }
        .summary-card .number { font-size: 24px; font-weight: bold; color: #0d9488; }
        .summary-card .label { font-size: 12px; color: #6b7280; }
        .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; text-align: center; }
      </style>
    </head><body>
      <h1>Supplier Scorecard Report - ${selectedQuarter}</h1>
      <p><strong>Generated:</strong> ${dateStr} | <strong>Suppliers:</strong> ${filteredSuppliers.length}</p>
      <div class="summary-grid">
        <div class="summary-card"><div class="number">${stats.preferred}</div><div class="label">Preferred</div></div>
        <div class="summary-card"><div class="number">${stats.avgDOT}%</div><div class="label">Avg DOT</div></div>
        <div class="summary-card"><div class="number" style="color: ${stats.atRisk > 0 ? '#dc2626' : '#0d9488'}">${stats.atRisk}</div><div class="label">At Risk</div></div>
      </div>
      <table><thead><tr><th>Supplier</th><th>Status</th><th>Region</th><th>DOT</th><th>COT</th><th>Fill Rate</th><th>Overall</th><th>Trend</th></tr></thead>
        <tbody>${filteredSuppliers.map(s => `<tr><td>${s.name}</td><td>${s.status}</td><td>${s.region}</td><td>${s.onTimeDelivery}%</td><td>${s.completeOrders}%</td><td>${s.fillRate}%</td><td>${s.overallScore}</td><td>${s.trendValue > 0 ? '+' : ''}${s.trendValue}%</td></tr>`).join('')}</tbody>
      </table>
      <div class="footer">Auxilio Logistics - Supplier Scorecard | ${selectedQuarter} | Generated on ${dateStr}</div>
    </body></html>`;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { printWindow.print(); }, 250);
    }
    setShowExportMenu(false);
  }, [filteredSuppliers, selectedQuarter, stats]);

  // Export functions for individual supplier
  const handleSupplierExportCSV = useCallback(() => {
    if (!selectedSupplier) return;
    const s = selectedSupplier;
    const headers = ['Field', 'Value'];
    const rows = [
      ['Supplier Name', s.name], ['Code', s.code], ['Category', s.category], ['Region', s.region], ['Status', s.status],
      ['Overall Score', s.overallScore], ['DOT %', s.onTimeDelivery], ['COT %', s.completeOrders], ['Fill Rate %', s.fillRate],
      ['Quality Score', s.qualityScore], ['Responsiveness', s.responsiveness], ['Total Shipments', s.totalShipments],
      ['Avg Lead Time (days)', s.avgLeadTime], ['Defect Rate %', s.defectRate], ['Monthly Volume', s.monthlyVolume],
      ['Yearly Spend ($)', s.yearlySpend], ['Contract Expiry', s.contractExpiry], ['Last Delivery', s.lastDelivery],
    ];
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `supplier_${s.code}_report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowSupplierExportMenu(false);
  }, [selectedSupplier]);

  const handleSupplierExportWord = useCallback(() => {
    if (!selectedSupplier) return;
    const s = selectedSupplier;
    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
      <head><meta charset="utf-8"><title>${s.name} Report</title>
        <style>body { font-family: Calibri; } h1 { color: #0d9488; } table { border-collapse: collapse; width: 100%; } th, td { border: 1px solid #ddd; padding: 8px; } th { background: #0d9488; color: white; }</style>
      </head><body>
        <h1>${s.name} - Supplier Report</h1>
        <p><strong>Generated:</strong> ${dateStr} | <strong>Code:</strong> ${s.code}</p>
        <h2>Performance Metrics</h2>
        <table><tr><th>Metric</th><th>Value</th></tr>
          <tr><td>Overall Score</td><td>${s.overallScore}</td></tr>
          <tr><td>Delivery On Time (DOT)</td><td>${s.onTimeDelivery}%</td></tr>
          <tr><td>Complete On Time (COT)</td><td>${s.completeOrders}%</td></tr>
          <tr><td>Fill Rate</td><td>${s.fillRate}%</td></tr>
          <tr><td>Quality Score</td><td>${s.qualityScore}%</td></tr>
          <tr><td>Defect Rate</td><td>${s.defectRate}%</td></tr>
        </table>
        <h2>Operational Details</h2>
        <table><tr><th>Detail</th><th>Value</th></tr>
          <tr><td>Total Shipments</td><td>${s.totalShipments.toLocaleString()}</td></tr>
          <tr><td>Average Lead Time</td><td>${s.avgLeadTime} days</td></tr>
          <tr><td>Monthly Volume</td><td>${s.monthlyVolume} orders</td></tr>
          <tr><td>Yearly Spend</td><td>$${(s.yearlySpend / 1000000).toFixed(2)}M</td></tr>
          <tr><td>Contract Expiry</td><td>${s.contractExpiry}</td></tr>
        </table>
      </body></html>`;
    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(blob));
    link.setAttribute('download', `supplier_${s.code}_report.doc`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowSupplierExportMenu(false);
  }, [selectedSupplier]);

  const handleSupplierExportPDF = useCallback(() => {
    if (!selectedSupplier) return;
    const s = selectedSupplier;
    const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const printContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${s.name} Report</title>
      <style>
        @media print { body { -webkit-print-color-adjust: exact; } }
        body { font-family: -apple-system, BlinkMacSystemFont, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
        h1 { color: #0d9488; border-bottom: 3px solid #0d9488; padding-bottom: 15px; }
        .metrics { display: grid; grid-template-columns: repeat(3, 1fr); gap: 15px; margin: 20px 0; }
        .metric { background: #f0fdfa; border: 1px solid #14b8a6; padding: 15px; border-radius: 8px; text-align: center; }
        .metric .value { font-size: 28px; font-weight: bold; color: #0d9488; }
        .metric .label { font-size: 12px; color: #6b7280; }
        table { border-collapse: collapse; width: 100%; margin-top: 15px; }
        th, td { border: 1px solid #d1d5db; padding: 10px; text-align: left; }
        th { background: #0d9488; color: white; }
      </style>
    </head><body>
      <h1>${s.name}</h1>
      <p><strong>Code:</strong> ${s.code} | <strong>Status:</strong> ${s.status} | <strong>Generated:</strong> ${dateStr}</p>
      <div class="metrics">
        <div class="metric"><div class="value">${s.overallScore}</div><div class="label">Overall Score</div></div>
        <div class="metric"><div class="value">${s.onTimeDelivery}%</div><div class="label">DOT</div></div>
        <div class="metric"><div class="value">${s.completeOrders}%</div><div class="label">COT</div></div>
      </div>
      <h2>Performance Details</h2>
      <table>
        <tr><th>Metric</th><th>Value</th></tr>
        <tr><td>Fill Rate</td><td>${s.fillRate}%</td></tr>
        <tr><td>Quality Score</td><td>${s.qualityScore}%</td></tr>
        <tr><td>Defect Rate</td><td>${s.defectRate}%</td></tr>
        <tr><td>Total Shipments</td><td>${s.totalShipments.toLocaleString()}</td></tr>
        <tr><td>Average Lead Time</td><td>${s.avgLeadTime} days</td></tr>
        <tr><td>Yearly Spend</td><td>$${(s.yearlySpend / 1000000).toFixed(2)}M</td></tr>
      </table>
    </body></html>`;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => { printWindow.print(); }, 250);
    }
    setShowSupplierExportMenu(false);
  }, [selectedSupplier]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Supplier Scorecard</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Monitor supplier performance with DOT, COT, and quality metrics
          </p>
        </div>
        <div className="flex gap-2">
          {/* Quarter Selector Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowQuarterMenu(!showQuarterMenu)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              <CalendarIcon className="w-4 h-4 mr-2" />
              {selectedQuarter}
              <ChevronDownIcon className="w-4 h-4 ml-2" />
            </button>
            {showQuarterMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowQuarterMenu(false)} />
                <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                  <div className="py-1">
                    {['Q1 2024', 'Q4 2023', 'Q3 2023', 'Q2 2023', 'Q1 2023', 'Q4 2022'].map(quarter => (
                      <button
                        key={quarter}
                        onClick={() => { setSelectedQuarter(quarter); setShowQuarterMenu(false); }}
                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${selectedQuarter === quarter ? 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                      >
                        {quarter}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Export Report Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              Export Report
              <ChevronDownIcon className="w-4 h-4 ml-2" />
            </button>
            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                  <div className="py-1">
                    <button onClick={handleExportCSV} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <TableCellsIcon className="w-4 h-4 mr-3 text-green-600" />Export as CSV
                    </button>
                    <button onClick={handleExportWord} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <DocumentTextIcon className="w-4 h-4 mr-3 text-blue-600" />Export as Word
                    </button>
                    <button onClick={handleExportPDF} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                      <DocumentArrowDownIcon className="w-4 h-4 mr-3 text-red-600" />Export as PDF
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards - Reordered: Total, Preferred, At Risk, Avg DOT, Avg COT, Avg Score */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <button
          onClick={() => setCardFilter(cardFilter === 'total' ? 'all' : 'total')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${cardFilter === 'total' ? 'border-teal-500 ring-2 ring-teal-500' : 'border-gray-100 dark:border-gray-700'}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Suppliers</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalSuppliers}</p>
            </div>
            <BuildingOffice2Icon className="w-8 h-8 text-teal-500" />
          </div>
        </button>
        <button
          onClick={() => setCardFilter(cardFilter === 'preferred' ? 'all' : 'preferred')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${cardFilter === 'preferred' ? 'border-green-500 ring-2 ring-green-500' : 'border-gray-100 dark:border-gray-700'}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Preferred</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.preferred}</p>
            </div>
            <StarSolidIcon className="w-8 h-8 text-green-500" />
          </div>
        </button>
        <button
          onClick={() => setCardFilter(cardFilter === 'atRisk' ? 'all' : 'atRisk')}
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border p-4 text-left transition-all hover:shadow-md ${cardFilter === 'atRisk' ? 'border-red-500 ring-2 ring-red-500' : 'border-gray-100 dark:border-gray-700'}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">At Risk</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{stats.atRisk}</p>
            </div>
            <ExclamationTriangleIcon className="w-8 h-8 text-red-500" />
          </div>
        </button>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Avg DOT</p>
              <p className={`text-2xl font-bold ${getScoreColor(stats.avgDOT)}`}>{stats.avgDOT}%</p>
            </div>
            <TruckIcon className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Avg COT</p>
              <p className={`text-2xl font-bold ${getScoreColor(stats.avgCOT)}`}>{stats.avgCOT}%</p>
            </div>
            <CheckCircleIcon className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Avg Score</p>
              <p className={`text-2xl font-bold ${getScoreColor(stats.avgScore)}`}>{stats.avgScore}</p>
            </div>
            <ChartBarIcon className="w-8 h-8 text-teal-500" />
          </div>
        </div>
      </div>

      {/* Performance Trend Chart with Period Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Network Performance Trend</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Average supplier metrics over time</p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Period Selector */}
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              {[
                { value: 'week', label: 'Week' },
                { value: 'month', label: 'Month' },
                { value: 'year', label: 'Year' },
                { value: '2years', label: '2 Years' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTrendPeriod(option.value as TrendPeriod)}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                    trendPeriod === option.value
                      ? 'bg-white dark:bg-gray-600 text-teal-600 dark:text-teal-400 shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            {/* Legend */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 bg-teal-500 rounded"></span>
                <span className="text-gray-600 dark:text-gray-300">DOT %</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 bg-blue-500 rounded"></span>
                <span className="text-gray-600 dark:text-gray-300">COT %</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="w-3 h-3 bg-green-500 rounded"></span>
                <span className="text-gray-600 dark:text-gray-300">Fill Rate %</span>
              </div>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="relative h-48">
          <div className="absolute inset-0 flex items-end justify-between">
            {currentPerformanceData.map((period, index) => (
              <div key={period.month} className="flex-1 flex flex-col items-center px-2">
                <div className="w-full relative h-36 flex items-end justify-center gap-1">
                  <div className="w-4 bg-teal-500 rounded-t" style={{ height: `${((period.dot - 85) / 15) * 100}%` }}></div>
                  <div className="w-4 bg-blue-500 rounded-t" style={{ height: `${((period.cot - 85) / 15) * 100}%` }}></div>
                  <div className="w-4 bg-green-500 rounded-t" style={{ height: `${((period.fillRate - 85) / 15) * 100}%` }}></div>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-2">{period.month}</span>
              </div>
            ))}
          </div>
          <div className="absolute left-0 top-0 h-36 flex flex-col justify-between text-xs text-gray-400">
            <span>100%</span>
            <span>92%</span>
            <span>85%</span>
          </div>
        </div>
      </div>

      {/* Filters with Sort and View Toggle */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search suppliers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Statuses</option>
              <option value="preferred">Preferred</option>
              <option value="approved">Approved</option>
              <option value="probation">Probation</option>
              <option value="suspended">Suspended</option>
            </select>
            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Regions</option>
              {regions.map((region) => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
            {/* Sort buttons with direction toggle */}
            <div className="flex items-center gap-1 border border-gray-300 dark:border-gray-600 rounded-lg p-1 bg-white dark:bg-gray-700">
              {[
                { value: 'overallScore' as const, label: 'Overall' },
                { value: 'onTimeDelivery' as const, label: 'DOT' },
                { value: 'completeOrders' as const, label: 'COT' },
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => handleSortChange(option.value)}
                  className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium transition-colors ${
                    sortBy === option.value
                      ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                >
                  {option.label}
                  {sortBy === option.value && (
                    sortDirection === 'desc'
                      ? <ChevronDownIcon className="w-3 h-3 ml-1" />
                      : <ChevronUpIcon className="w-3 h-3 ml-1" />
                  )}
                </button>
              ))}
            </div>
            {/* View Mode Toggle */}
            <div className="flex gap-1 border border-gray-300 dark:border-gray-600 rounded-lg p-1 bg-white dark:bg-gray-700">
              <div className="relative group">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`p-2 rounded transition-colors ${viewMode === 'cards' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                  aria-label="Card View"
                >
                  <Squares2X2Icon className="w-4 h-4" />
                </button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  Card View
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                </div>
              </div>
              <div className="relative group">
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-colors ${viewMode === 'list' ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
                  aria-label="List View"
                >
                  <TableCellsIcon className="w-4 h-4" />
                </button>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                  List View
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-3 text-sm text-gray-500 dark:text-gray-400">
          Showing {filteredSuppliers.length} of {mockSuppliers.length} suppliers
        </div>
      </div>

      {/* Supplier Cards or List View */}
      {viewMode === 'cards' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredSuppliers.map((supplier) => (
            <div
              key={supplier.id}
              onClick={() => setSelectedSupplier(supplier)}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 cursor-pointer hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center">
                    <BuildingOffice2Icon className="w-6 h-6 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{supplier.name}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{supplier.code} • {supplier.category}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {getStatusBadge(supplier.status)}
                      <span className="text-xs text-gray-500 dark:text-gray-400">{supplier.region}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-3xl font-bold ${getScoreColor(supplier.overallScore)}`}>{supplier.overallScore}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Overall Score</p>
                  <div className="mt-1">{getTrendIcon(supplier.trend, supplier.trendValue)}</div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">DOT</p>
                  <p className={`text-lg font-bold ${getScoreColor(supplier.onTimeDelivery)}`}>{supplier.onTimeDelivery}%</p>
                  <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full mt-1">
                    <div className={`h-full rounded-full ${getScoreBgColor(supplier.onTimeDelivery)}`} style={{ width: `${supplier.onTimeDelivery}%` }}></div>
                  </div>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">COT</p>
                  <p className={`text-lg font-bold ${getScoreColor(supplier.completeOrders)}`}>{supplier.completeOrders}%</p>
                  <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full mt-1">
                    <div className={`h-full rounded-full ${getScoreBgColor(supplier.completeOrders)}`} style={{ width: `${supplier.completeOrders}%` }}></div>
                  </div>
                </div>
                <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Fill Rate</p>
                  <p className={`text-lg font-bold ${getScoreColor(supplier.fillRate)}`}>{supplier.fillRate}%</p>
                  <div className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-full mt-1">
                    <div className={`h-full rounded-full ${getScoreBgColor(supplier.fillRate)}`} style={{ width: `${supplier.fillRate}%` }}></div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                  <span>{supplier.totalShipments.toLocaleString()} shipments</span>
                  <span>•</span>
                  <span>{supplier.avgLeadTime}d lead time</span>
                  <span>•</span>
                  <span>{supplier.defectRate}% defect rate</span>
                </div>
                {renderStars(supplier.overallScore)}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Supplier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Region</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">DOT</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">COT</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fill Rate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Overall</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Trend</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredSuppliers.map((supplier) => (
                  <tr key={supplier.id} onClick={() => setSelectedSupplier(supplier)} className="hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mr-3">
                          <BuildingOffice2Icon className="w-5 h-5 text-gray-500" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{supplier.name}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{supplier.code}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(supplier.status)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{supplier.region}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-semibold ${getScoreColor(supplier.onTimeDelivery)}`}>{supplier.onTimeDelivery}%</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-semibold ${getScoreColor(supplier.completeOrders)}`}>{supplier.completeOrders}%</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-sm font-semibold ${getScoreColor(supplier.fillRate)}`}>{supplier.fillRate}%</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`text-lg font-bold ${getScoreColor(supplier.overallScore)}`}>{supplier.overallScore}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{getTrendIcon(supplier.trend, supplier.trendValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Supplier Detail Modal */}
      {selectedSupplier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center">
                    <BuildingOffice2Icon className="w-7 h-7 text-gray-500 dark:text-gray-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedSupplier.name}</h2>
                    <p className="text-gray-500 dark:text-gray-400">{selectedSupplier.code}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {getStatusBadge(selectedSupplier.status)}
                      <span className="text-sm text-gray-500 dark:text-gray-400">{selectedSupplier.region}</span>
                    </div>
                  </div>
                </div>
                <button onClick={() => setSelectedSupplier(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Overall Score */}
              <div className="flex items-center justify-center gap-8 p-6 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                <div className="text-center">
                  <p className={`text-5xl font-bold ${getScoreColor(selectedSupplier.overallScore)}`}>{selectedSupplier.overallScore}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Overall Score</p>
                  <div className="mt-2">{renderStars(selectedSupplier.overallScore)}</div>
                </div>
                <div className="h-20 w-px bg-gray-300 dark:bg-gray-600"></div>
                <div className="text-center">
                  {getTrendIcon(selectedSupplier.trend, selectedSupplier.trendValue)}
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">vs Last Quarter</p>
                </div>
              </div>

              {/* Key Metrics */}
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Key Performance Metrics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Delivery On Time (DOT)</p>
                    <p className={`text-2xl font-bold ${getScoreColor(selectedSupplier.onTimeDelivery)}`}>{selectedSupplier.onTimeDelivery}%</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Complete On Time (COT)</p>
                    <p className={`text-2xl font-bold ${getScoreColor(selectedSupplier.completeOrders)}`}>{selectedSupplier.completeOrders}%</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Fill Rate</p>
                    <p className={`text-2xl font-bold ${getScoreColor(selectedSupplier.fillRate)}`}>{selectedSupplier.fillRate}%</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Quality Score</p>
                    <p className={`text-2xl font-bold ${getScoreColor(selectedSupplier.qualityScore)}`}>{selectedSupplier.qualityScore}%</p>
                  </div>
                </div>
              </div>

              {/* Additional Details */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Operational Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Total Shipments</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedSupplier.totalShipments.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Average Lead Time</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedSupplier.avgLeadTime} days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Defect Rate</span>
                      <span className={`text-sm font-medium ${selectedSupplier.defectRate < 1 ? 'text-green-600' : selectedSupplier.defectRate < 2 ? 'text-yellow-600' : 'text-red-600'}`}>{selectedSupplier.defectRate}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Monthly Volume</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedSupplier.monthlyVolume} orders</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Contract Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Category</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedSupplier.category}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Contract Expiry</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedSupplier.contractExpiry}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Yearly Spend</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">${(selectedSupplier.yearlySpend / 1000000).toFixed(2)}M</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">Last Delivery</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedSupplier.lastDelivery}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-between">
              <button
                onClick={() => {
                  setShipmentsSupplier(selectedSupplier);
                  setShowShipmentsModal(true);
                }}
                className="px-4 py-2 border border-teal-500 text-teal-600 dark:text-teal-400 rounded-lg hover:bg-teal-50 dark:hover:bg-teal-900/20"
              >
                View All Shipments
              </button>
              <div className="flex gap-2">
                <button onClick={() => setSelectedSupplier(null)} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                  Close
                </button>
                {/* Download Report Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowSupplierExportMenu(!showSupplierExportMenu)}
                    className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                  >
                    Download Report
                    <ChevronDownIcon className="w-4 h-4 ml-2" />
                  </button>
                  {showSupplierExportMenu && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowSupplierExportMenu(false)} />
                      <div className="absolute right-0 bottom-full mb-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                        <div className="py-1">
                          <button onClick={handleSupplierExportCSV} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <TableCellsIcon className="w-4 h-4 mr-3 text-green-600" />Export as CSV
                          </button>
                          <button onClick={handleSupplierExportWord} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <DocumentTextIcon className="w-4 h-4 mr-3 text-blue-600" />Export as Word
                          </button>
                          <button onClick={handleSupplierExportPDF} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                            <DocumentArrowDownIcon className="w-4 h-4 mr-3 text-red-600" />Export as PDF
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shipments Modal */}
      {showShipmentsModal && shipmentsSupplier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Shipments from {shipmentsSupplier.name}</h2>
                <p className="text-gray-500 dark:text-gray-400">{shipmentsSupplier.totalShipments.toLocaleString()} total shipments</p>
              </div>
              <button onClick={() => { setShowShipmentsModal(false); setShipmentsSupplier(null); }} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Shipment ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Origin</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Destination</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Pallets</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">On Time</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {generateSupplierShipments(shipmentsSupplier).map((shipment) => (
                      <tr key={shipment.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{shipment.id}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{shipment.date}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{shipment.origin}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{shipment.destination}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{shipment.pallets}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            shipment.status === 'delivered' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                            shipment.status === 'in_transit' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                            'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                          }`}>
                            {shipment.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {shipment.onTime ? (
                            <CheckCircleIcon className="w-5 h-5 text-green-500" />
                          ) : (
                            <XCircleIcon className="w-5 h-5 text-red-500" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => { setShowShipmentsModal(false); setShipmentsSupplier(null); }}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierScorecard;
